/**
 * Org marketplace -> Leads (docs/MarketplaceDesign.md section 3.5; M2): the
 * organization's lead counts and outcomes per vendor, without members'
 * contact details, and voiding a fake or duplicate lead with a note.
 */
import { useCallback, useEffect, useState } from 'react';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import LeadPill from './LeadPill';
import { Spinner, ErrorAlert, EmptyState, formatDate } from './ui';
import { MISMATCH_LABELS, PURCHASE_LABELS, formatUsd } from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

export default function OrgLeadsPanel() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [note, setNote] = useState('');
  const [resolving, setResolving] = useState(null);
  const [finding, setFinding] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await marketApi.listOrgLeads());
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmVoid(lead) {
    if (!note.trim()) {
      toast.error('Say why the lead is void');
      return;
    }
    try {
      await marketApi.actOnLead(lead.leadId, 'void', { note: note.trim() });
      toast.success('Lead voided');
      setVoiding(null);
      setNote('');
      load();
    } catch (err) {
      toast.error(err.message || 'Nothing changed');
    }
  }

  async function resolve(lead) {
    if (!finding.trim()) {
      toast.error('Say what you found');
      return;
    }
    try {
      await marketApi.resolveMismatch(lead.leadId, finding.trim());
      toast.success('Resolved');
      setResolving(null);
      setFinding('');
      load();
    } catch (err) {
      toast.error(err.message || 'Nothing changed');
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!data) return <Spinner label="Loading leads..." />;
  if (!data.leads.length)
    return <EmptyState message="No member has joined an interest list yet." />;
  return (
    <section>
      <div
        className={cn(ui.card, ui.tableWrap, 'mb-6')}
        tabIndex={0}
        role="region"
        aria-label="Leads by vendor"
      >
        <table className={ui.table}>
          <thead>
            <tr>
              <th scope="col">Vendor</th>
              <th scope="col">Leads</th>
              <th scope="col">Qualified</th>
              <th scope="col">Won</th>
              <th scope="col">Win rate</th>
              <th scope="col">Reported deals</th>
              <th scope="col">Open mismatches</th>
            </tr>
          </thead>
          <tbody>
            {data.vendors.map(v => (
              <tr key={v.vendorId}>
                <td>{v.vendorName}</td>
                <td>{v.summary.total}</td>
                <td>{v.summary.counts.qualified}</td>
                <td>{v.summary.counts.won}</td>
                <td>{v.summary.winRate === null ? '-' : `${v.summary.winRate}%`}</td>
                <td>{formatUsd(v.summary.dealValueCents)}</td>
                <td>
                  {v.mismatches}
                  {v.reviewSuggested && (
                    <span className={cn(ui.pillWarning, 'ml-2')}>Review the listing</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <section aria-labelledby="quality-heading" className="mb-6">
        <h2 id="quality-heading" className="mt-0 text-title-m">
          Quality queue
        </h2>
        <p className={cn(ui.hint, 'mt-0')}>
          Follow-up answers that disagree with what the vendor recorded. Repeated mismatches are a
          reason to review a listing.
        </p>
        {(data.mismatches || []).length === 0 ? (
          <p className={ui.muted}>Nothing to look at.</p>
        ) : (
          <ul className="m-0 list-none p-0" aria-label="Mismatches">
            {data.mismatches.map(l => (
              <li key={l.leadId} className={cn(ui.cardFlat, 'mb-2')}>
                <strong>{l.vendorName}</strong> - {l.serviceName} <LeadPill status={l.status} />
                <span className="block text-body-s">
                  {MISMATCH_LABELS[l.mismatch] || l.mismatch}
                </span>
                <span className={cn(ui.hint, 'block')}>
                  Member: {PURCHASE_LABELS[l.followUp?.purchased] || '-'}
                  {Number.isInteger(l.followUp?.pricePaidCents)
                    ? `, paid about ${formatUsd(l.followUp.pricePaidCents)}`
                    : ''}
                  {Number.isInteger(l.dealValueCents)
                    ? `; vendor recorded ${formatUsd(l.dealValueCents)}`
                    : ''}
                </span>
                {resolving === l.leadId ? (
                  <div className={cn(ui.row, 'mt-2')}>
                    <label className="flex-1">
                      <span className="sr-only">What you found</span>
                      <input
                        className={ui.input}
                        value={finding}
                        maxLength={1000}
                        placeholder="What you found"
                        onChange={e => setFinding(e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className={cn(ui.btnPrimary, ui.btnSm)}
                      onClick={() => resolve(l)}
                    >
                      Resolve
                    </button>
                    <button
                      type="button"
                      className={cn(ui.btnGhost, ui.btnSm)}
                      onClick={() => setResolving(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={cn(ui.btnSecondary, ui.btnSm, 'mt-2')}
                    onClick={() => setResolving(l.leadId)}
                  >
                    Resolve
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <ul className="m-0 list-none p-0" aria-label="Leads">
        {data.leads.map(l => (
          <li key={l.leadId} className="border-b border-z-line py-2">
            <div className={cn(ui.row, ui.spread)}>
              <span>
                <strong>{l.vendorName}</strong> - {l.serviceName} <LeadPill status={l.status} />
                <span className={cn(ui.hint, 'block')}>
                  {formatDate(l.createdAt)}
                  {l.categoryName ? ` - ${l.categoryName}` : ''}
                  {l.status === 'won' && Number.isInteger(l.dealValueCents)
                    ? ` - deal ${formatUsd(l.dealValueCents)}`
                    : ''}
                  {l.statusNote ? ` - ${l.statusNote}` : ''}
                </span>
              </span>
              {l.actions.includes('void') && voiding !== l.leadId && (
                <button
                  type="button"
                  className={cn(ui.btnGhost, ui.btnSm)}
                  onClick={() => setVoiding(l.leadId)}
                >
                  Void
                </button>
              )}
            </div>
            {voiding === l.leadId && (
              <div className={cn(ui.row, 'mt-2')}>
                <label className="flex-1">
                  <span className="sr-only">Why this lead is void</span>
                  <input
                    className={ui.input}
                    value={note}
                    maxLength={1000}
                    placeholder="Why: a duplicate, a fake sign-up..."
                    onChange={e => setNote(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className={cn(ui.btnDanger, ui.btnSm)}
                  onClick={() => confirmVoid(l)}
                >
                  Void lead
                </button>
                <button
                  type="button"
                  className={cn(ui.btnGhost, ui.btnSm)}
                  onClick={() => setVoiding(null)}
                >
                  Cancel
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
